import { API, errorOf } from '../hermes';

export const EXECUTOR_KEY = 'agent_bench';
export const TEMPLATE_NAME = 'Agent bench';
export const DEPLOYED = 'deployed';
export const SOURCE_TYPES = ['session', 'ticket', 'pylon', 'hat', 'staff'];
export const VERDICTS = ['met', 'partly', 'not_met'];
export const VERDICT_LABEL = { met: 'Met', partly: 'Partly', not_met: 'Not met' };

function list(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value || '').split('\n').map((v) => v.trim()).filter(Boolean);
}

export function caseFrom(c, i) {
  const source = c.source && typeof c.source === 'object' ? c.source : {};
  return {
    kind: 'case', key: String(c.key || ''), label: String(c.label || `case ${i + 1}`), tenant: String(c.tenant || ''),
    prompt: String(c.prompt || ''), hats: String(c.hats || ''), user_context: String(c.user_context || ''),
    answer: String(c.answer || ''), must: list(c.must), must_not: list(c.must_not), truth_author: String(c.truth_author || ''),
    source: { type: SOURCE_TYPES.includes(source.type) ? source.type : 'session', ref: String(source.ref || c.source_session || ''), note: String(source.note || '') },
    rubric: String(c.rubric || ''), source_session: String(c.source_session || ''), source_run: c.source_run || null,
  };
}

export function hasTruth(c) {
  return Boolean(c.answer.trim() || c.must.length || c.must_not.length || c.rubric.trim());
}

export function variantFrom(v) {
  return {
    kind: 'variant', label: String(v.label || ''), description: String(v.description || ''), model: String(v.model || ''),
    thinking: String(v.thinking || ''), hats_enabled: String(v.hats_enabled || ''), system_prompt: String(v.system_prompt || ''),
    hats: String(v.hats || ''), tools: String(v.tools || ''),
  };
}

export async function findBench(axios) {
  const { data } = await axios.get(`${API}/console/bench/`);
  return { executor: data.executor, template: data.template, tenantSlug: data.tenant_slug, tenantName: data.tenant_name,
    defaultModel: data.default_model, defaultReasoning: data.default_reasoning, defaultJudgeRepeats: data.default_judge_repeats || 1 };
}

export function onBench(bench, config = {}) {
  return { ...config, excludeTenantSlug: true, params: { ...(config.params || {}), tenant_slug: bench.tenantSlug } };
}

export function casesOf(template) {
  return (template?.inputs || []).filter((x) => x.kind === 'case').map(caseFrom);
}

export function variantsOf(template) {
  return (template?.inputs || []).filter((x) => x.kind === 'variant').map(variantFrom);
}

async function saveInputs(axios, bench, kind, items) {
  const others = (bench.template.inputs || []).filter((x) => x.kind !== kind);
  const { data } = await axios.patch(`${API}/templates/${bench.template.id}/`, { inputs: others.concat(items) }, onBench(bench));
  return data;
}

export function saveCases(axios, bench, cases) {
  return saveInputs(axios, bench, 'case', cases.map(caseFrom));
}

export function saveVariants(axios, bench, variants) {
  return saveInputs(axios, bench, 'variant', variants.map(variantFrom));
}

export function percent(x) {
  return typeof x === 'number' ? `${Math.round(x * 100)}%` : '—';
}

export function signedPoints(x) {
  if (typeof x !== 'number') return '—';
  const p = Math.round(x * 1000) / 10;
  return `${p > 0 ? '+' : ''}${p} pts`;
}

export function paramsLine(run) {
  const p = run.params || {};
  if (p.rejudge) return `rejudge #${p.rejudge}${p.judge_repeats ? ` × ${p.judge_repeats}` : ''}`;
  const bits = [(p.variants || DEPLOYED).split(',').join(' vs ')];
  if (p.repeats > 1) bits.push(`× ${p.repeats}`);
  if (p.judge_repeats) bits.push(`judge × ${p.judge_repeats}`);
  if (p.model) bits.push(p.model);
  if (p.reasoning) bits.push(p.reasoning);
  if (p.hats) bits.push(`hats ${p.hats}`);
  if (p.only) bits.push(`only ${p.only.split(',').length}`);
  return bits.join(' · ');
}

export function scoresLine(run) {
  const s = (run.metadata || {}).scores;
  if (s && Object.keys(s).length) return Object.entries(s).map(([v, x]) => (Object.keys(s).length > 1 ? `${v} ${percent(x)}` : percent(x))).join(' · ');
  const legacy = (run.metadata || {}).judge_avg;
  return legacy === null || legacy === undefined ? '—' : `${legacy} / 5`;
}

export function progressLine(run) {
  const pr = run.progress || {};
  if (!pr.units) return '';
  return `${pr.done ?? 0} of ${pr.units} ran${pr.failed ? ` · ${pr.failed} failed to run` : ''}`;
}

export { errorOf };
